/**
 * REST client + store for everything the live task docs cannot answer:
 * dashboard stats, the forever-kept completed history, achievements and API
 * keys. Also owns the achievement celebration loop: local completions
 * schedule a debounced /check; unseen unlocks queue a celebration card and
 * are then marked seen (server-persisted, so a pop happens once per unlock
 * across all devices).
 */
import { create } from "zustand";
import type { AchievementDef, AchievementDto, ApiKeyDto, TaskHistoryItemDto, TaskStatsDto } from "@hydra-notes/shared";
import { useAuth } from "./auth";
import { celebrateBig } from "@/tasks/celebrate";

/** Minutes EAST of UTC (what the server's day-boundary math expects). */
export function tzOffsetMin(): number {
  return -new Date().getTimezoneOffset();
}

async function api<T>(path: string, init?: RequestInit): Promise<T | null> {
  try {
    const res = await fetch(path, { credentials: "same-origin", ...init });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

interface TaskStatsState {
  stats: TaskStatsDto | null;
  statsLoading: boolean;
  achievements: AchievementDto[];
  history: TaskHistoryItemDto[];
  historyCursor: string | null;
  historyLoading: boolean;
  keys: ApiKeyDto[];
  /** Celebration queue rendered by <AchievementPops/>. */
  popQueue: AchievementDef[];
  refreshStats: () => Promise<void>;
  refreshAchievements: () => Promise<void>;
  loadHistory: (opts?: { reset?: boolean; listId?: string }) => Promise<void>;
  refreshKeys: () => Promise<void>;
  createKey: (label: string, scope: "read" | "write") => Promise<ApiKeyDto | null>;
  revokeKey: (id: string) => Promise<void>;
  shiftPop: () => void;
}

export const useTaskStats = create<TaskStatsState>((set, get) => ({
  stats: null,
  statsLoading: false,
  achievements: [],
  history: [],
  historyCursor: null,
  historyLoading: false,
  keys: [],
  popQueue: [],

  refreshStats: async () => {
    if (useAuth.getState().status !== "signedIn") return;
    set({ statsLoading: true });
    const stats = await api<TaskStatsDto>(`/api/tasks/stats?tzOffset=${tzOffsetMin()}`);
    set({ statsLoading: false, ...(stats ? { stats } : {}) });
  },

  refreshAchievements: async () => {
    if (useAuth.getState().status !== "signedIn") return;
    const res = await api<{ achievements: AchievementDto[] }>(`/api/tasks/achievements?tzOffset=${tzOffsetMin()}`);
    if (res) set({ achievements: res.achievements });
  },

  loadHistory: async (opts) => {
    const { historyCursor, history, historyLoading } = get();
    if (historyLoading) return;
    const cursor = opts?.reset ? null : historyCursor;
    set({ historyLoading: true });
    const params = new URLSearchParams();
    if (cursor) params.set("cursor", cursor);
    if (opts?.listId) params.set("listId", opts.listId);
    const res = await api<{ items: TaskHistoryItemDto[]; nextCursor: string | null }>(
      `/api/tasks/history?${params.toString()}`,
    );
    set({
      historyLoading: false,
      ...(res
        ? { history: opts?.reset ? res.items : [...history, ...res.items], historyCursor: res.nextCursor }
        : {}),
    });
  },

  refreshKeys: async () => {
    const res = await api<{ keys: ApiKeyDto[] }>("/api/api-keys");
    if (res) set({ keys: res.keys });
  },

  createKey: async (label, scope) => {
    const res = await api<{ key: ApiKeyDto }>("/api/api-keys", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ label, scope }),
    });
    if (res) void get().refreshKeys();
    return res?.key ?? null;
  },

  revokeKey: async (id) => {
    await api(`/api/api-keys/${id}`, { method: "DELETE" });
    void get().refreshKeys();
  },

  shiftPop: () => set((s) => ({ popQueue: s.popQueue.slice(1) })),
}));

// ---------- achievement celebration loop ----------

let checkTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Called after any local completion (debounced: a burst of checkmarks does
 * one round trip). Unseen unlocks celebrate exactly once: the seen mark goes
 * back BEFORE the pop so a re-check cannot double-fire.
 */
export function scheduleAchievementCheck(): void {
  if (useAuth.getState().status !== "signedIn") return;
  if (checkTimer) clearTimeout(checkTimer);
  checkTimer = setTimeout(() => {
    checkTimer = null;
    void runAchievementCheck();
  }, 2_000);
}

async function runAchievementCheck(): Promise<void> {
  const res = await api<{ unseen: AchievementDef[] }>(`/api/tasks/achievements/check?tzOffset=${tzOffsetMin()}`, {
    method: "POST",
  });
  if (!res?.unseen.length) return;
  await api("/api/tasks/achievements/seen", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ keys: res.unseen.map((a) => a.key) }),
  });
  celebrateBig();
  useTaskStats.setState((s) => ({ popQueue: [...s.popQueue, ...res.unseen] }));
}
